import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { USER_GUIDES, GUIDE_CATEGORIES, CATEGORY_COLORS } from '../../data/userGuides';

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || '#6B7280';
  return (
    <span
      className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: color }}
    >
      {category}
    </span>
  );
}

function GuideCard({ guide }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <CategoryBadge category={guide.category} />
        <span className="text-xs text-mid-grey whitespace-nowrap">{guide.readingTime} min read</span>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-brand-navy leading-snug">{guide.title}</h3>
        <span className="inline-block mt-1 text-xs text-mid-grey bg-gray-100 px-2 py-0.5 rounded-full">
          {guide.audience}
        </span>
      </div>
      <p className="text-xs text-mid-grey leading-relaxed flex-1">{guide.summary}</p>
      <Link
        to={`/guides/${guide.slug}`}
        className="text-xs font-semibold text-brand-teal hover:underline self-start"
      >
        Read guide →
      </Link>
    </div>
  );
}

export default function GuideLibrary() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = USER_GUIDES.filter((g) => {
    const matchesCategory = activeCategory === 'All' || g.category === activeCategory;
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      g.title.toLowerCase().includes(q) ||
      g.summary.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-mid-grey mb-5">
        <span className="text-brand-navy font-medium">Guide Library</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-navy mb-1">End-User Training Guides</h1>
        <p className="text-sm text-mid-grey">
          Ready-made guides for distributing to your clients' staff. Browse, read, and deploy to client plans.
        </p>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mid-grey"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeWidth="2"
              strokeLinecap="round"
              d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1016.65 16.65z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search guides…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
          />
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2">
          {['All', ...GUIDE_CATEGORIES].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                activeCategory === cat
                  ? 'bg-brand-navy text-white border-brand-navy'
                  : 'bg-white text-mid-grey border-gray-200 hover:border-brand-navy hover:text-brand-navy'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((guide) => (
            <GuideCard key={guide.slug} guide={guide} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <svg
            className="w-10 h-10 text-gray-300 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeWidth="1.5"
              strokeLinecap="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-mid-grey text-sm">No guides match your search.</p>
          <button
            onClick={() => { setSearch(''); setActiveCategory('All'); }}
            className="mt-2 text-xs text-brand-teal hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
